import React, { useState, useEffect, useMemo, useRef } from "react";
import { Card, Col, Button, Form, Row, Breadcrumb, Spinner, Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faMapMarkerAlt, faPhoneAlt, faFileInvoice, faArrowRight } from "@fortawesome/free-solid-svg-icons"
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios"; // Import Axios
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useAppContext } from "providers/appProvider";
import { fetchRoleAndClaims, handleLoadingAndAccess } from "../../assets/js/common";
import { generateRelationNumber } from "assets/js/common";
import Select from "react-select";
import { useLocationRedirect, createAuthorizationUrl, openPopup, getAuthorizationCode, exchangeCodeForToken, updateAsperionToken, handleCreateAsperionRelationConfirmationMessgae } from 'assets/js/common'
import { getDomainConfig } from "../../config/domainConfig";
import "react-phone-number-input/style.css";
import { getCountries } from "react-phone-number-input";
import { getExampleNumber, getCountryCallingCode } from "libphonenumber-js/max";
import examples from "libphonenumber-js/examples.mobile.json";
import CustomCountrySelect from "components/common/CustomCountrySelect";

const NewRelation = () => {
  const { t, i18n } = useTranslation();
  const domainConfig = getDomainConfig();
  const finLabels = domainConfig.financialLabels;

  const defaultCountryCode = (domainConfig.defaultPhoneCountry || "in").toUpperCase();
  const [selectedMobileCountry, setSelectedMobileCountry] = useState(defaultCountryCode);
  const [selectedLandlineCountry, setSelectedLandlineCountry] = useState(defaultCountryCode);

  const mobilePlaceholder = useMemo(() => {
    const example = getExampleNumber(selectedMobileCountry, examples);
    return example ? example.formatNational() : "";
  }, [selectedMobileCountry]);

  const mobileCallingCode = useMemo(() => {
    try {
      return getCountryCallingCode(selectedMobileCountry);
    } catch {
      return "";
    }
  }, [selectedMobileCountry]);

  const phonePlaceholder = useMemo(() => {
    const example = getExampleNumber(selectedLandlineCountry, examples);
    return example ? example.formatNational() : "";
  }, [selectedLandlineCountry]);

  const phoneCallingCode = useMemo(() => {
    try {
      return getCountryCallingCode(selectedLandlineCountry);
    } catch {
      return "";
    }
  }, [selectedLandlineCountry]);

  // Validation Schema
  const schema = yup
    .object({
      name: yup.string().required(t("nameRequired")), // Name is required
      type: yup.string().required(t("typeRequired")), // Type is required
      cocNumber: yup
        .string()
        .nullable(), // Allows it to be empty
      vatNumber: yup
        .string()
        .nullable(), // Allows it to be empty
      status: yup.string().required(t("statusRequired")), // Status is required
      salutation: yup.string().nullable(),
      firstName: yup.string().required(t("firstNameRequired")),
      lastName: yup.string().nullable(),
      streetName: yup.string().required(t("streetNameRequired")), // Street name is required
      houseNumber: yup.string().nullable(), // House number is required
      houseAddition: yup.string().nullable(), // House addition (optional)
      postcode: yup
        .string()
        .required(t("zipcodeRequired"))
        .matches(getDomainConfig().postcodeRegex, t("validPostCode")),
      city: yup.string().required(t("cityRequired")), // City is required
      country: yup.string().required(t("countryRequired")), // Country is required
      email1: yup
        .string()
        .email(t("invalidEmail")) // Invalid email address
        .required(t("emailRequired")), // Email address is required
      email2: yup.string().email(t("invalidEmail")).nullable(), // Second email address (optional)
      mobileNumber: yup
        .string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .test("has-country-code", t("enterValidMobileNumber"), function (value) {
          if (!value) return true;
          if (!value.startsWith("+")) return false;
          const digitsOnly = value.replace(/[^\d]/g, "");
          return digitsOnly.length > 0 && digitsOnly.length <= 20;
        }),
      phoneNumber: yup
        .string()
        .nullable()
        .transform((value) => (value === "" ? null : value))
        .test("has-country-code", t("enterValidPhoneNumber"), function (value) {
          if (!value) return true;
          if (!value.startsWith("+")) return false;
          const digitsOnly = value.replace(/[^\d]/g, "");
          return digitsOnly.length > 0 && digitsOnly.length <= 20;
        }),
      invoiceEmail: yup
        .string()
        .email(t("invalidInvoiceEmail")) // Invalid invoice email address
        .required(t("invoiceEmailRequired")), // Invoice email is required

      iban: yup.string().nullable(), // Allows it to be empty
      accountDetails: yup.string().nullable(),
      ifscDetails: yup.string().nullable(),
      gstDetails: yup.string().nullable(),
    })
    .required(); // Entire schema is required

  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      status: "0",
      country: domainConfig.countries[0]?.value || "",  // ← from config
      type: "1",
      soort: "Zakelijk",
    },
  });

  const {
    handleSubmit,
    register,
    watch,
    control,
    formState: { errors },
    reset,
    trigger,
    setValue, clearErrors,
  } = methods;

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const fieldsWithErrors = Object.keys(errors);
      trigger(fieldsWithErrors);
    }
  }, [i18n.language]);

  const email1 = watch("email1");

  useEffect(() => {
    if (!isInvoiceEdited) {
      setValue("invoiceEmail", email1);
    }
  }, [email1]);

  const navigate = useNavigate();
  const { config: { location } } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [activeToggles, setActiveToggles] = useState(false);
  const selectedType = watch("type"); // Watch for changes to 'type'
  const selectedCountry = watch("country");
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [relationId, setRelationId] = useState();
  const [asperionUserId, setAsperionUserId] = useState(null);
  const [isAsperionEnabled, setIsAsperionEnabled] = useState(false)
  const [isInvoiceEdited, setIsInvoiceEdited] = useState(false);
  const [inviteToPortal, setInviteToPortal] = useState(false);

  // --- Address Autocomplete states ---
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [showStreetSuggestions, setShowStreetSuggestions] = useState(false);
  const [isFetchingStreet, setIsFetchingStreet] = useState(false);
  const [isFetchingPostcode, setIsFetchingPostcode] = useState(false);
  const debounceRef = useRef(null);
  const postcodeDebounceRef = useRef(null);

  const getCountryCode = (countryValue) => {
    const found = domainConfig.countries.find((c) => c.value === countryValue);
    return found?.isoCode?.toLowerCase() || found?.value?.toLowerCase() || "";
  };

  const fetchAddressSuggestions = async (query, countryValue) => {
    if (!query || query.length < 3) {
      setStreetSuggestions([]);
      return;
    }
    const countryCode = getCountryCode(countryValue);
    setIsFetchingStreet(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/addressRoute/search`,
        {
          params: { query, countryCode },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        }
      );
      setStreetSuggestions(response.data || []);
      setShowStreetSuggestions(true);
    } catch (error) {
      console.error("Address suggestion fetch failed:", error);
      setStreetSuggestions([]);
    } finally {
      setIsFetchingStreet(false);
    }
  };

  const countryOptions = domainConfig.countries.map(c => ({
    value: c.value,
    label: t(c.label),
  }));

  const countrySelectOptions = useMemo(() => {
    return getCountries().map((country) => ({
      value: country,
      label: country,
    }));
  }, []);

  const fetchCityFromPostcode = async (postcode, countryValue) => {
    if (!postcode) return;
    const countryCode = getCountryCode(countryValue);
    setIsFetchingPostcode(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/addressRoute/postcode`,
        {
          params: { postcode, countryCode },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
        }
      );
      const data = response.data;
      if (data && data[0]) {
        const addr = data[0].address || {};
        const city = addr.city || addr.town || addr.village || addr.county || "";
        if (city) {
          setValue("city", city, { shouldValidate: true });
        }
      }
    } catch (error) {
      console.error("Postcode lookup failed:", error);
    } finally {
      setIsFetchingPostcode(false);
    }
  };

  const handleStreetInputChange = (value, countryValue) => {
    setValue("streetName", value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchAddressSuggestions(value, countryValue);
    }, 400);
  };

  const handlePostcodeInputChange = (value, countryValue) => {
    setValue("postcode", value);
    if (postcodeDebounceRef.current) clearTimeout(postcodeDebounceRef.current);
    postcodeDebounceRef.current = setTimeout(() => {
      fetchCityFromPostcode(value, countryValue);
    }, 500);
  };

  const handleSelectSuggestion = (place) => {
    const addr = place.address || {};

    // Try dedicated street fields first.
    let streetName =
      addr.road ||
      addr.pedestrian ||
      addr.footway ||
      addr.cycleway ||
      addr.residential ||
      addr.street ||
      addr.path ||
      addr.living_street ||
      "";

    // Fall back to locality fields if no street is available.
    if (!streetName) {
      streetName =
        addr.neighbourhood ||
        addr.suburb ||
        addr.quarter ||
        addr.hamlet ||
        "";
    }

    // Extract the street from display_name as a last resort.
    if (!streetName && place.display_name) {
      const partsToRemove = [
        addr.house_number,
        addr.postcode,
        addr.city,
        addr.town,
        addr.village,
        addr.county,
        addr.state_district,
        addr.state,
        addr.country,
      ].filter(Boolean);

      const displayParts = place.display_name
        .split(",")
        .map((p) => p.trim())
        .filter((p) => !partsToRemove.some((rm) => p.toLowerCase() === rm.toLowerCase()));

      streetName = displayParts[0] || "";
    }

    setValue("streetName", streetName, { shouldValidate: true });

    if (addr.house_number) setValue("houseNumber", addr.house_number, { shouldValidate: true });
    if (addr.postcode) setValue("postcode", addr.postcode, { shouldValidate: true });

    const city =
      addr.city ||
      addr.town ||
      addr.village ||
      addr.county ||
      addr.state_district ||
      "";
    if (city) setValue("city", city, { shouldValidate: true });

    setStreetSuggestions([]);
    setShowStreetSuggestions(false);
  };

  const steps = [
    { id: 1, title: t("clientInformation"), subtitle: t("basicInfoSub") },
    { id: 2, title: t("addressDetails"), subtitle: t("addressDetailsSub") },
    { id: 3, title: t("contactDetails"), subtitle: t("contactDetailsSub") },
    { id: 4, title: t("invoiceDetails"), subtitle: t("invoiceDetailsSub") },
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [activeAccordionKey, setActiveAccordionKey] = useState("1");

  const stepFields = {
    1: ["name", "type", "status", "soort"],
    2: ["salutation", "firstName", "lastName", "streetName", "houseNumber", "houseAddition", "postcode", "city", "country"],
    3: ["email1", "email2", "phoneNumber", "mobileNumber"],
    4: ["invoiceEmail", "iban", "accountDetails", "ifscDetails", "gstDetails", "cocNumber", "vatNumber"],
  };

  const requiredFieldsByStep = {
    1: ["name", "type", "status"],
    2: ["firstName", "streetName", "postcode", "city", "country"],
    3: ["email1"],
    4: ["invoiceEmail"],
  };

  const [completedSteps, setCompletedSteps] = useState([]);
  const watchedValues = watch();

  useEffect(() => {
    setCompletedSteps((prev) => {
      const next = prev.filter((stepId) => {
        const requiredFields = requiredFieldsByStep[stepId] || [];
        if (requiredFields.length === 0) return true;
        return requiredFields.every((field) => {
          const val = watchedValues[field];
          return val !== undefined && val !== null && val !== "";
        });
      });
      return next.length === prev.length ? prev : next;
    });
  }, [watchedValues]);

  const otherStepsCompleted = useMemo(() => {
    return [1, 2, 3, 4]
      .filter((id) => id !== currentStep)
      .every((id) => completedSteps.includes(id));
  }, [completedSteps, currentStep]);

  useEffect(() => {
    fetchAsperionSettingsEnable();
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (postcodeDebounceRef.current) clearTimeout(postcodeDebounceRef.current);
    };
  }, []);

  const fetchAsperionSettingsEnable = async () => {
    try {
      const customerId = localStorage.getItem("customerId");
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/settingsRoute/asperionSync/${customerId}`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      setIsAsperionEnabled(response.data?.AsperionSyncEnabled)
    } catch (error) {
      console.error("Error fetching Asperion settings:", error);
    }
  };

  useEffect(() => {
    if (selectedType !== "0") {
      clearErrors(["cocNumber", "vatNumber", "iban"]); // Clear errors for these fields
      setValue("cocNumber", ""); // Reset field values
      setValue("vatNumber", "");
      setValue("iban", "");
    }
  }, [selectedType, clearErrors, setValue]);
  useLocationRedirect("relations");

  useEffect(() => {
    // Check if user has permission to create relations
    fetchRoleAndClaims({
      navigate,
      claimType: "relations",
      claimValue: "create",
      setHasAccess,
      setIsLoading,
      t,
    });
  }, [navigate]);

  useEffect(() => {
    // Get AsperionUserId from location API
    const fetchTokenUsingAsperionUser = async () => {
      try {
        if (!location?.LocationId) return;

        const locationId = location.LocationId;

        const locationDetails = await axios.get(
          `${process.env.REACT_APP_API_URL}/locationRoute/getById/${locationId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );

        const asperionUserId = locationDetails.data.AsperionUserId;

        setAsperionUserId(asperionUserId); // optional if you're using this state
        fetchAsperionToken(asperionUserId);
      } catch (error) {
        console.error("Failed to fetch AsperionUserId from location:", error);
      }
    };

    if (isAsperionEnabled) {
      fetchTokenUsingAsperionUser();
    }
  }, [navigate, location, isAsperionEnabled]);

  const handleDiscard = () => {
    reset();
    navigate("/relations");
  };

  const handleNext = async () => {
    const valid = await trigger(stepFields[currentStep]);
    if (!valid) {
      toast.error(t("pleaseFillRequiredFields"), {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    setCurrentStep((step) => {
      const nextStep = Math.min(step + 1, 4);
      setActiveAccordionKey(String(nextStep));
      return nextStep;
    });
  };

  const handleSave = async () => {
    const valid = await trigger(stepFields[currentStep]);
    if (valid) {
      setCompletedSteps((prev) => [...new Set([...prev, currentStep])]);
    }
    handleSubmit(onSubmit, onInvalid)();
  };

  const handleBack = () => {
    setCurrentStep((step) => {
      const prevStep = Math.max(step - 1, 1);
      setActiveAccordionKey(String(prevStep));
      return prevStep;
    });
  };

  const onInvalid = (formErrors) => {
    const errorFields = Object.keys(formErrors);
    if (errorFields.length === 0) return;

    const stepWithError = Object.keys(stepFields).find((stepId) =>
      stepFields[stepId].some((field) => errorFields.includes(field))
    );

    if (stepWithError) {
      setCurrentStep(Number(stepWithError));
      setActiveAccordionKey(stepWithError);
    }

    toast.error(t("pleaseFillRequiredFields"), {
      position: "top-right",
      autoClose: 3000,
    });
  };

  const goToStep = (stepId) => {
    if (stepId === currentStep) return;
    setCurrentStep(stepId);
    setActiveAccordionKey(String(stepId));
  };

  const fetchAsperionToken = async (asperionUserId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/asperionAutoTokenRoute/getToken/${asperionUserId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      const tokens = response.data;
      if (!tokens) {
        setTokenData(null);
        return;
      }

      const expiry = new Date(tokens.TokenExpiresAt).getTime();
      const now = Date.now();
      const isExpired = expiry < now;

      if (isExpired) {
        console.warn("Access token expired, attempting to refresh...");
        const refreshed = await refreshAccessToken(tokens.RefreshToken, asperionUserId);
        setTokenData(refreshed);
        setAccessToken(refreshed.AccessToken);
        setRefreshToken(refreshed.RefreshToken);
      } else {
        setTokenData(tokens);
        setAccessToken(tokens.AccessToken);
        setRefreshToken(tokens.RefreshToken);
      }
    } catch (err) {
      console.error("Failed to fetch token:", err);
      toast.error(t("asperionfetchTokenError"), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleCreateRelationConfirmationMessgae = () => {
    reset();
    toast.success(t("relationshipAdded"), {
      position: "top-right",
      autoClose: 5000,
    });
    navigate("/relations");
    setIsLoading(false)
  }

  const handleToggleChange = (checked) => {
    setActiveToggles(checked);
  };

  const onSubmit = async (data) => {

    setIsLoading(true);

    const customerId = localStorage.getItem("customerId");
    if (!customerId) {
      setIsLoading(false);
      console.error("CustomerId is missing.");
      return;
    }

    const relationNumber = await generateRelationNumber();
    const asperionSource = activeToggles ? 1 : 0; // Set AsperionSource based on toggle state
    const updatedFormData = { ...data, relationNumber, asperionSource, locationId: location?.LocationId || null, customerId }; // Include AsperionSource

    try {
      // Send the form data to the backend to create the relation
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/relationRoute/relation`, updatedFormData, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );

      // If the relation is created successfully
      if (response.status === 201) {
        setRelationId(response.data.RelationId)
        const newRelationId = response.data.RelationId;

        if (inviteToPortal) {
          try {
            const userId = localStorage.getItem("UserId");
            const culture = localStorage.getItem("culture");
            await axios.post(
              `${process.env.REACT_APP_API_URL}/relationRoute/inviteToCustomerPortal/${newRelationId}`,
              { performedBy: userId || null, culture: culture || null },
              { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );
          } catch (inviteError) {
            console.error("Error inviting relation to customer portal:", inviteError);

            try {
              await axios.delete(
                `${process.env.REACT_APP_API_URL}/relationRoute/deleteRelation/${newRelationId}`,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
              );
            } catch (rollbackError) {
              console.error("Failed to roll back relation after invite failure:", rollbackError);
            }

            const messageKey =
              inviteError.response?.data?.messageKey ||
              inviteError.response?.data?.message ||
              "somethingWentWrong";
            toast.error(t(messageKey), { position: "top-right", autoClose: 5000 });
            setIsLoading(false);
            return;
          }
        }

        // Create debtor if the type is "0" and toggle is active
        if (isAsperionEnabled && data.type === "0" && activeToggles) {
          if (tokenData) {
            setIsLoading(false)
            await createDebtorInAsperion(updatedFormData, accessToken, refreshToken, newRelationId)
          } else {
            setIsLoading(false)
            const authorizationUrl = createAuthorizationUrl();
            const popup = openPopup(authorizationUrl);

            if (!popup) {
              console.error("Failed to open popup. Please allow popups for this site.");
              return;
            }

            const authorizationCode = await getAuthorizationCode(popup);
            exchangeCodeForToken(authorizationCode).then(async (tokenData) => {
              const tokenDataBody = {
                asperionUserId: asperionUserId,
                accessToken: tokenData.access_token,
                refreshToken: tokenData.refresh_token,
                expiresIn: tokenData.expires_in,
              };

              setTokenData(tokenData);
              setAccessToken(tokenData.access_token)
              setRefreshToken(tokenData.refresh_token)
              await updateAsperionToken(tokenDataBody);
              await createDebtorInAsperion(updatedFormData, tokenData.access_token, tokenData.refresh_token, newRelationId);
            })
          }
        }
        else {
          setIsLoading(false)
          handleCreateRelationConfirmationMessgae();
        }
      }
    }
    catch (error) {
      setIsLoading(false)
      // Check if the error response contains a message
      if (error.response && error.response.data && error.response.status === 400) {
        toast.error(t("relationAlreadyExistError"), {
          position: "top-right",
          autoClose: 5000,
        });
      } else {
        toast.error(t("relationAlreadyExistError"), {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect; // Render the loading spinner or handle navigation
  }

  const refreshAccessToken = async (refreshToken, asperionUserId) => {
    if (!asperionUserId || !refreshToken) {
      console.error("Missing asperionUserId or refreshToken");
      toast.error("Unable to refresh token. Please log in again.");
      throw new Error("Missing required token data.");
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/asperionAutoTokenRoute/refreshToken`,
        {
          asperionUserId,
          refreshToken,
        },
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );

      const tokens = response.data;

      setAccessToken(tokens.AccessToken);
      setRefreshToken(tokens.RefreshToken);
      setTokenData(tokens);

      return tokens;
    } catch (error) {
      console.error("Failed to refresh access token:", error);
      toast.error("Failed to refresh access token.", {
        position: "top-right",
        autoClose: 5000,
      });
      throw error;
    }
  };

  const createDebtorInAsperion = async (data, accessToken, refreshToken, newRelationId) => {
    setIsLoading(true);
    let tokens;
    let paymentCondition;
    let paymentConditionId;
    const locationId = location.LocationId;

    const locationDetails = await axios.get(
      `${process.env.REACT_APP_API_URL}/locationRoute/getById/${locationId}`,
      { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
    );

    // Fetch payment conditions (with 401 retry)
    try {
      paymentCondition = await axios.get(
        `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/fetchpaymentConditions`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            "x-tenant-id": locationDetails.data.AsperionTenantId,
          },
        }
      );
      paymentConditionId = paymentCondition.data._data[0].id;
    } catch (error) {
      if (error.response && error.response.status === 401) {
        tokens = await refreshAccessToken(refreshToken, asperionUserId);
        refreshToken = tokens.RefreshToken;
        accessToken = tokens.AccessToken;
        paymentCondition = await axios.get(
          `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/fetchpaymentConditions`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              "x-tenant-id": locationDetails.data.AsperionTenantId,
            },
          }
        );
        paymentConditionId = paymentCondition.data._data[0].id;
      } else {
        throw error;
      }
    }

    // Build the debtor payload
    const asperionData = {
      cocNumber: data.cocNumber,
      directDebit: true,
      iban: data.iban || null,
      vatNumber: data.vatNumber || null,
      address: data.streetName,
      postCode: data.postcode,
      countryCode: "NL",
      paymentConditionId: paymentConditionId,
      phoneNumber: data.phoneNumber || "",
      mobileNumber: data.mobileNumber || "",
      emailAddress: data.email1,
      name: data.name,
      city: data.city,
      contacts: [
        {
          firstName: data.firstName,
          name: data.lastName || "",
          emailAddress: data.email1,
          phoneNumber: data.phoneNumber || "",
          mobileNumber: data.mobileNumber || "",
        },
      ],
    };

    const createdrelationId = relationId || newRelationId;

    // Create the debtor (with 401 retry)
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/post`,
        asperionData,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
            "x-tenant-id": locationDetails.data.AsperionTenantId,
          },
        }
      );

      if (response.status === 200) {
        const asperionDebtordata = {
          asperionRelationId: response.data.id,
          asperionRelationCustomerNumber: response.data.customerNumber,
        };
        if (createdrelationId) {
          await axios.patch(
            `${process.env.REACT_APP_API_URL}/relationRoute/updateRelationByAsperiondata/${createdrelationId}`,
            asperionDebtordata,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
        }
        handleCreateAsperionRelationConfirmationMessgae(setIsLoading, t("debtorCreatedSuccess"));
        handleCreateRelationConfirmationMessgae();
        setIsLoading(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        tokens = await refreshAccessToken(refreshToken, asperionUserId);
        accessToken = tokens.AccessToken;
        const resp = await axios.post(
          `${process.env.REACT_APP_API_URL}/asperionDebtorRoute/post`,
          asperionData,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
              "x-tenant-id": locationDetails.data.AsperionTenantId,
            },
          }
        );
        const asperionDebtordata = {
          asperionRelationId: resp.data.id,
          asperionRelationCustomerNumber: resp.data.customerNumber,
        };
        if (createdrelationId) {
          await axios.patch(
            `${process.env.REACT_APP_API_URL}/relationRoute/updateRelationByAsperiondata/${createdrelationId}`,
            asperionDebtordata,
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          );
        }
        handleCreateAsperionRelationConfirmationMessgae(setIsLoading, t("debtorCreatedSuccess"));
        handleCreateRelationConfirmationMessgae();
      } else {
        setIsLoading(false);
        handleCreateRelationConfirmationMessgae();
        toast.error("An error occurred while creating the debtor in Asperion.", {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };
  const typeOptions = [
    { value: "0", label: t("client") },
    { value: "1", label: t("prospect") },
  ];

  if (isLoading || !hasAccess) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <FormProvider {...methods}>
        <Row className="g-3 mb-3">
          <Col lg={12} xxl={12}>
            <Card className="mb-3 h-100">
              <Background
                image={corner4}
                className="bg-card"
                style={{
                  borderTopRightRadius: "0.375rem",
                  borderBottomRightRadius: "0.375rem",
                }}
              />
              <Card.Body className="position-relative pe-4 pb-2">
                <Row className="flex-between-center">
                  <Col md>
                    <h5 className="mb-2 mb-md-0"> {t("newrelation")}</h5>
                    <Breadcrumb>
                      <Breadcrumb.Item
                        as={Link}
                        to="/relations"
                        className="fw-semi-bold mb-1 fs-9 data-font-colour"
                        active
                      >
                        {t("relation")}
                      </Breadcrumb.Item>
                      <Breadcrumb.Item className="fw-bold mb-1 fs-9 data-font-colour" active>
                        {t("new")}
                      </Breadcrumb.Item>
                    </Breadcrumb>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Form onSubmit={(e) => e.preventDefault()}>
          <Row className="g-3 align-items-stretch">
            {/* Left Sidebar */}
            <Col xs="12" lg="3" xl="2" className="rf-left-panel step-sidebar-col">
              <Card className="mb-3 h-100 step-row-container">
                <Card.Body>
                  {steps.map((step, index) => (
                    <div
                      key={step.id}
                      lang={i18n.language}
                      className={`d-flex gap-2 pointer p-2 rounded step-row ${currentStep === step.id ? "step-row-active" : ""
                        }`}
                      onClick={() => goToStep(step.id)}
                    >
                      <div className="d-flex flex-column align-items-center step-circle-col">
                        <span
                          className={`step-circle ${currentStep === step.id ? "active" : ""} ${completedSteps.includes(step.id) ? "completed" : ""
                            }`}
                        >
                          {step.id}
                        </span>
                        {index < steps.length - 1 && (
                          <div
                            className={`step-connector-line ${completedSteps.includes(step.id) ? "completed" : ""
                              } ${currentStep === step.id ? "active" : ""}`}
                          />
                        )}
                      </div>
                      <div className="step-text-col">
                        <h5
                          className={`fw-medium ${currentStep === step.id ? "text-primary" : ""
                            }`}
                        >
                          {step.title}
                        </h5>
                        <small className="mt-2 text-secondary">{step.subtitle}</small>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            {/* Right Side */}
            <Col xs="12" lg="9" xl="10" className="rf-right-panel rf-form-col">
              <Accordion
                activeKey={activeAccordionKey}
                onSelect={(key) => {
                  if (key) goToStep(Number(key));
                }}
                className="rf-accordion"
              >
                <Accordion.Item eventKey="1" className="rf-accordion-item">
                  {/* Klantgegevens Section */}
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center">
                      <span className="rf-header-icon bg-client">
                        <FontAwesomeIcon icon={faUser} />
                      </span>
                      <span className="rf-header-text">
                        <span>{t("clientInformation")}</span>
                        <span className="rf-header-subtitle">{t("basicInfoSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour mb-2">
                            {t("name")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            size="md"
                            placeholder={getDomainConfig().namePlaceholder}
                            {...register("name")}
                            isInvalid={!!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("type")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="type"
                            control={control}
                            rules={{ required: t("typeRequired") }}
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Select
                                  key={i18n.language}
                                  placeholder={t("select")}
                                  noOptionsMessage={() => t("noOptionsAvailable")}
                                  {...field}
                                  isClearable
                                  options={typeOptions}
                                  value={typeOptions.find(opt => opt.value === field.value) || null}
                                  onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                  className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                  classNamePrefix="custom-select"
                                />
                                {error && (
                                  <div className="invalid-feedback d-block">{error.message}</div>
                                )}
                              </>
                            )}
                          />
                        </Form.Group>
                      </Col>

                      <Col md="3">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour d-block">
                            {t("customerPortal")}
                          </Form.Label>
                          <Form.Check
                            type="checkbox"
                            id="invite-portal-checkbox"
                            label={t("inviteToCustomerPortal")}
                            checked={inviteToPortal}
                            onChange={(e) => setInviteToPortal(e.target.checked)}
                          />
                        </Form.Group>
                      </Col>

                      {/* Toggle for creating debtor in Asperion */}
                      {isAsperionEnabled && selectedType === "0" && (
                        <Col md={{ offset: "5", span: "4" }} className="d-flex align-items-center pt-2">
                          <Form.Label className="d-flex align-items-start">
                            <span>
                              <Form.Check
                                className="custom-green-switch"
                                type="switch"
                                id="vat-toggle-switch"
                                checked={activeToggles}
                                onChange={(e) => handleToggleChange(e.target.checked)}
                              />
                            </span>
                            <p className="fw-semi-bold mb-1 me-4 data-font-size data-font-colour text-nowrap">
                              {t("createNewDebtorInAsperion")}
                            </p>
                          </Form.Label>
                        </Col>
                      )}
                    </Row>

                    <Row className="mb-3">
                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("status")}<span className="text-danger">*</span>
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="status-active"
                              label={t("active")}
                              value="0"
                              {...register("status")}
                              isInvalid={!!errors.status}
                            />
                            <Form.Check
                              type="radio"
                              id="status-inactive"
                              label={t("inactive")}
                              value="1"
                              {...register("status")}
                              isInvalid={!!errors.status}
                            />
                          </div>
                          {errors.status && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.status.message}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>

                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("soort")}
                          </Form.Label>
                          <div className="d-flex gap-3">
                            <Form.Check
                              type="radio"
                              id="particulier-radio"
                              label={t("individual")}
                              value="Particulier"
                              {...register("soort")}
                            />
                            <Form.Check
                              type="radio"
                              id="zakelijk-radio"
                              label={t("business")}
                              value="Zakelijk"
                              {...register("soort")}
                            />
                          </div>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2" className="rf-accordion-item">
                  {/* Adresgegevens Section */}
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                      <span className="rf-header-icon bg-address">
                        <FontAwesomeIcon icon={faMapMarkerAlt} />
                      </span>
                      <span className="rf-header-text">
                        <span>{t("addressDetails")}</span>
                        <span className="rf-header-subtitle">{t("addressDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col xs="12" sm="4" md="3" xl="2" className="salutation-col">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("salutation")}
                          </Form.Label>
                          <Controller
                            name="salutation"
                            control={control}
                            render={({ field, fieldState: { error } }) => {
                              const salutationOptions = [
                                { value: "Mr", label: t("mr") },
                                { value: "Mrs", label: t("mrs") },
                                { value: "Ms", label: t("ms") },
                                { value: "Dr", label: t("dr") },
                              ];
                              return (
                                <>
                                  <Select
                                    placeholder={t("select")}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                    isClearable
                                    options={salutationOptions}
                                    value={salutationOptions.find(o => o.value === field.value) || null}
                                    onChange={(sel) => field.onChange(sel ? sel.value : "")}
                                    className={`compact-select-container custom-select-container ${error ? "is-invalid" : ""}`}
                                    classNamePrefix="custom-select"
                                  />
                                  {error && <div className="invalid-feedback d-block">{error.message}</div>}
                                </>
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("firstName")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control type="text" {...register("firstName")} isInvalid={!!errors.firstName} placeholder="John" />
                          <Form.Control.Feedback type="invalid">{errors.firstName?.message}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("lastName")}
                          </Form.Label>
                          <Form.Control type="text" {...register("lastName")} isInvalid={!!errors.lastName} placeholder="Doe" />
                          <Form.Control.Feedback type="invalid">{errors.lastName?.message}</Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("streetName")}<span className="text-danger">*</span>
                          </Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              type="text"
                              autoComplete="off"
                              {...register("streetName")}
                              onChange={(e) => handleStreetInputChange(e.target.value, selectedCountry)}
                              onFocus={() => streetSuggestions.length > 0 && setShowStreetSuggestions(true)}
                              onBlur={() => setTimeout(() => setShowStreetSuggestions(false), 200)}
                              isInvalid={!!errors.streetName}
                              placeholder={getDomainConfig().streetPlaceholder}
                            />
                            {isFetchingStreet && (
                              <div className="position-absolute top-0 end-0 mt-2 me-2">
                                <Spinner animation="border" size="sm" />
                              </div>
                            )}
                            {showStreetSuggestions && streetSuggestions.length > 0 && (
                              <div className="address-suggestions-menu">
                                {streetSuggestions.map((place, index) => (
                                  <div
                                    key={place.place_id ?? index}
                                    className="address-suggestion-item"
                                    onMouseDown={() => handleSelectSuggestion(place)}
                                  >
                                    {place.display_name}
                                  </div>
                                ))}
                              </div>
                            )}
                            <Form.Control.Feedback type="invalid">
                              {errors.streetName?.message}
                            </Form.Control.Feedback>
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md="2">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour text-nowrap">
                            {t("houseNumber")}
                          </Form.Label>
                          <Form.Control
                            type="text"
                            {...register("houseNumber")}
                            isInvalid={!!errors.houseNumber}
                            placeholder="12"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.houseNumber?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col md={finLabels.showAccount ? 4 : (finLabels.showKvk ? 2 : 4)}>
                        {/* Addition field - wider for India (md=4) */}
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("addition")}
                          </Form.Label>
                          <Form.Control type="text" {...register("houseAddition")} />
                        </Form.Group>
                      </Col>

                      {!finLabels.showAccount && (
                        <Col md="2">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t("postcode")}<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type="text"
                                {...register("postcode")}
                                onChange={(e) => handlePostcodeInputChange(e.target.value, selectedCountry)}
                                isInvalid={!!errors.postcode}
                                placeholder={getDomainConfig().postcodePlaceholder}
                              />
                              {isFetchingPostcode && (
                                <div className="position-absolute top-0 end-0 mt-2 me-2">
                                  <Spinner animation="border" size="sm" />
                                </div>
                              )}
                              <Form.Control.Feedback type="invalid" className="text-nowrap">
                                {errors.postcode?.message}
                              </Form.Control.Feedback>
                            </div>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>

                    <Row className="mb-3">
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("stad")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            {...register("city")}
                            isInvalid={!!errors.city}
                            placeholder={getDomainConfig().cityPlaceholder}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.city?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      {finLabels.showAccount && (
                        <Col md="2">
                          {/* Postcode field - moved here, between City and Country, for India */}
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t("postcode")}<span className="text-danger">*</span>
                            </Form.Label>
                            <div className="position-relative">
                              <Form.Control
                                type="text"
                                {...register("postcode")}
                                onChange={(e) => handlePostcodeInputChange(e.target.value, selectedCountry)}
                                isInvalid={!!errors.postcode}
                                placeholder={getDomainConfig().postcodePlaceholder}
                              />
                              {isFetchingPostcode && (
                                <div className="position-absolute top-0 end-0 mt-2 me-2">
                                  <Spinner animation="border" size="sm" />
                                </div>
                              )}
                              <Form.Control.Feedback type="invalid">
                                {errors.postcode?.message}
                              </Form.Control.Feedback>
                            </div>
                          </Form.Group>
                        </Col>
                      )}

                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("country")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Controller
                            name="country"
                            control={control}
                            rules={{ required: t("countryRequired") }}
                            render={({ field, fieldState: { error } }) => {
                              return (
                                <>
                                  <Select
                                    key={i18n.language} // re-renders on language change
                                    {...field}
                                    options={countryOptions}
                                    isClearable
                                    value={countryOptions.find((opt) => opt.value === field.value) || null}
                                    onChange={(selected) => field.onChange(selected ? selected.value : "")}
                                    className={`custom-select-container ${error ? "is-invalid" : ""}`}
                                    classNamePrefix="custom-select"
                                    placeholder={t("select")}
                                    noOptionsMessage={() => t("noOptionsAvailable")}
                                  />
                                  {error && (
                                    <div className="invalid-feedback d-block">{error.message}</div>
                                  )}
                                </>
                              );
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="3" className="rf-accordion-item">
                  {/* Contactgegevens Section */}
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                      <span className="rf-header-icon bg-contact">
                        <FontAwesomeIcon icon={faPhoneAlt} />
                      </span>
                      <span className="rf-header-text">
                        <span>{t("contactDetails")}</span>
                        <span className="rf-header-subtitle">{t("contactDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("emailAddress1")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            {...register("email1")}
                            isInvalid={!!errors.email1}
                            placeholder="john.doe@example.com"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email1?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("mobileNumber")}
                          </Form.Label>
                          <Controller
                            name="mobileNumber"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value, ref } }) => {
                              const prefix = `+${mobileCallingCode}`;
                              const displayValue =
                                value && value.startsWith(prefix)
                                  ? value.slice(prefix.length)
                                  : value || "";

                              return (
                                <div className={`phone-field-wrapper ${errors.mobileNumber ? "is-invalid" : ""}`}>
                                  <span className="phone-country-select-slot">
                                    <CustomCountrySelect
                                      value={selectedMobileCountry}
                                      options={countrySelectOptions}
                                      onChange={(country) => {
                                        if (!country) return;
                                        const oldPrefix = `+${mobileCallingCode}`;
                                        const rawDigits =
                                          value && value.startsWith(oldPrefix)
                                            ? value.slice(oldPrefix.length)
                                            : value || "";
                                        let newCallingCode = "";
                                        try {
                                          newCallingCode = getCountryCallingCode(country);
                                        } catch {
                                          newCallingCode = "";
                                        }
                                        setSelectedMobileCountry(country);
                                        onChange(rawDigits ? `+${newCallingCode}${rawDigits}` : "");
                                      }}
                                    />
                                  </span>
                                  <span className="phone-code-display">+{mobileCallingCode}</span>
                                  <input
                                    ref={ref}
                                    type="text"
                                    className="phone-number-input"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      onChange(raw ? `${prefix}${raw}` : "");
                                    }}
                                    placeholder={mobilePlaceholder}
                                    maxLength={25}
                                    name="mobileNumber"
                                  />
                                </div>
                              );
                            }}
                          />
                          {errors.mobileNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.mobileNumber.message}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>

                    </Row>
                    <Row className="mb-3">
                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("emailAddress2")}
                          </Form.Label>
                          <Form.Control
                            type="email"
                            {...register("email2")}
                            placeholder="johndoe.secondary@example.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md="4">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("phoneNumber")}
                          </Form.Label>
                          <Controller
                            name="phoneNumber"
                            control={control}
                            defaultValue=""
                            render={({ field: { onChange, value, ref } }) => {
                              const prefix = `+${phoneCallingCode}`;
                              const displayValue =
                                value && value.startsWith(prefix)
                                  ? value.slice(prefix.length)
                                  : value || "";

                              return (
                                <div className={`phone-field-wrapper ${errors.phoneNumber ? "is-invalid" : ""}`}>
                                  <span className="phone-country-select-slot">
                                    <CustomCountrySelect
                                      value={selectedLandlineCountry}
                                      options={countrySelectOptions}
                                      onChange={(country) => {
                                        if (!country) return;
                                        const oldPrefix = `+${phoneCallingCode}`;
                                        const rawDigits =
                                          value && value.startsWith(oldPrefix)
                                            ? value.slice(oldPrefix.length)
                                            : value || "";
                                        let newCallingCode = "";
                                        try {
                                          newCallingCode = getCountryCallingCode(country);
                                        } catch {
                                          newCallingCode = "";
                                        }
                                        setSelectedLandlineCountry(country);
                                        onChange(rawDigits ? `+${newCallingCode}${rawDigits}` : "");
                                      }}
                                    />
                                  </span>
                                  <span className="phone-code-display">+{phoneCallingCode}</span>
                                  <input
                                    ref={ref}
                                    type="text"
                                    className="phone-number-input"
                                    value={displayValue}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      onChange(raw ? `${prefix}${raw}` : "");
                                    }}
                                    placeholder={phonePlaceholder}
                                    maxLength={25}
                                    name="phoneNumber"
                                  />
                                </div>
                              );
                            }}
                          />
                          {errors.phoneNumber && (
                            <Form.Control.Feedback type="invalid" className="d-block">
                              {errors.phoneNumber.message}
                            </Form.Control.Feedback>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="4" className="rf-accordion-item">
                  {/* Factuurgegevens Section */}
                  <Accordion.Header>
                    <span className="h5 fw-medium mb-0 d-flex align-items-center data-font-colour">
                      <span className="rf-header-icon bg-invoice">
                        <FontAwesomeIcon icon={faFileInvoice} />
                      </span>
                      <span className="rf-header-text">
                        <span>{t("invoiceDetails")}</span>
                        <span className="rf-header-subtitle">{t("invoiceDetailsSub")}</span>
                      </span>
                    </span>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Row className="mb-3">
                      <Col md="5">
                        <Form.Group>
                          <Form.Label className="data-font-size data-font-colour">
                            {t("invoiceEmail")}<span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="email"
                            {...register("invoiceEmail")}
                            onChange={(e) => {
                              setIsInvoiceEdited(true);
                              setValue("invoiceEmail", e.target.value, { shouldValidate: true });
                            }}
                            isInvalid={!!errors.invoiceEmail}
                            placeholder="john.doe@example.com"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.invoiceEmail?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      {/* GST — IN only */}
                      {finLabels.showGst && (
                        <Col md="4">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t(finLabels.gst)}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              {...register("gstDetails")}
                              placeholder={finLabels.gstPlaceholder || ""}
                              isInvalid={!!errors.gstDetails}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.gstDetails?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}

                      {/* COC — NL only */}
                      {finLabels.showKvk && (
                        <Col md="4">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t("cocNumber")}
                            </Form.Label>
                            <Form.Control type="text" {...register("cocNumber")}
                              isInvalid={!!errors.cocNumber}
                              placeholder={finLabels.cocNumberPlaceholder || ""}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.cocNumber?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>

                    <Row className="mb-3">
                      {/* IBAN — NL only */}
                      {finLabels.showGAccount && (
                        <Col md="5">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t(finLabels.iban)}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              {...register("iban")}
                              isInvalid={!!errors.iban}
                              placeholder={finLabels.ibanPlaceholder || ""}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.iban?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}

                      {/* VAT — NL only */}
                      {finLabels.showKvk && (
                        <Col md="4">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t(finLabels.vat)}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              {...register("vatNumber")}
                              placeholder={finLabels.vatPlaceholder || ""}
                              isInvalid={!!errors.vatNumber}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.vatNumber?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}

                      {/* Account Number — IN only */}
                      {finLabels.showAccount && (
                        <Col md="5">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t(finLabels.account)}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              {...register("accountDetails")}
                              placeholder={finLabels.accountPlaceholder || ""}
                              isInvalid={!!errors.accountDetails}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.accountDetails?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                      {/* IFSC — IN only */}
                      {finLabels.showIfsc && (
                        <Col md="3">
                          <Form.Group>
                            <Form.Label className="data-font-size data-font-colour">
                              {t(finLabels.ifsc)}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              {...register("ifscDetails")}
                              placeholder={finLabels.ifscPlaceholder || ""}
                              isInvalid={!!errors.ifscDetails}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.ifscDetails?.message}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              <Card className="mt-2">
                <Card.Body>
                  <Row className="justify-content-between align-items-center">
                    <Col xs="auto">
                      <Button variant="falcon-default" size="sm" type="button" onClick={handleDiscard}>
                        {t("cancel")}
                      </Button>
                    </Col>
                    <Col xs="auto">
                      {currentStep > 1 && (
                        <Button variant="falcon-default" size="sm" className="me-3" type="button" onClick={handleBack}>
                          {t("back")}
                        </Button>
                      )}
                      {currentStep < 4 && !otherStepsCompleted ? (
                        <Button variant="primary" size="sm" type="button" onClick={handleNext}>
                          {t("next")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                        </Button>
                      ) : (
                        <Button data-tour="save-btn" variant="primary" size="sm" type="button" onClick={handleSave} disabled={isLoading}>
                          {isLoading ? t("saving") : (<>{t("save")} <FontAwesomeIcon icon={faArrowRight} className="ms-1" /></>)}
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </>
  );
};

export default NewRelation;